import React, { useState, useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShopperContext } from '../context/shopper';
import * as Constants from '../constants';
import SkuForm from './SkuForm';
import InAHurry from '../components/InAHurry';
import HeadingBuilder from '../components/HeadingBuilder';
import { useTranslation } from "react-i18next";
import styled from 'styled-components';
import skuMultipackSrc from "../assets/pic_sku_discreteb.jpeg";
import skuSingularSrc from "../assets/pic_sku_granularb.jpeg";
import skuByWeightSrc from "../assets/pic_sku_by_weightb.jpeg";

const SkuPic = styled.img`
    width: 120px;
    height: 120px;
    margin: 10px;
`;

const SkuChooseType = () => {
  const { signedIn, circles, foundSkus, stagedSku,
        } = useContext(ShopperContext);
  const [byWeightFormFlag, setByWeightFormFlag] = useState(false);
  const [multipackFormFlag, setMultipackFormFlag] = useState(false);
  const [singularFormFlag, setSingularFormFlag] = useState(false);
  const [haveNotHandledUuidYet, setHaveNotHandledUuidYet] = useState(true);
  const [focusSkuType, setFocusSkuType] = useState('');

  const {circle_id, chosenUuid} = useParams();
  console.log('SkuChooseType.js circle_id: ', circle_id)
  console.log('SkuChooseType.js chosenUuid: ', chosenUuid)
  const navigate = useNavigate();

  const searchRef      = useRef(null);
  const multipackSkuRef = useRef(null);
  const singularSkuRef = useRef(null);
  const weightySkuRef  = useRef(null);

  useEffect(() => {
    if (signedIn) {
      if (chosenUuid) {
        switch (focusSkuType) {
          case 'MultipackItem':
            multipackSkuRef.current.focus();
            break;
          case 'SingularItem':
            singularSkuRef.current.focus();
            break;
          case 'WeightyItem':
            weightySkuRef.current.focus();
            break;
          default:
            searchRef.current.focus();
        }
      }
      else {
        searchRef.current.focus();
      }
    }
  }, [signedIn, chosenUuid, focusSkuType]);

  const resetSkuTypeFormFlags = () => {
      setByWeightFormFlag(false);
      setMultipackFormFlag(false);
      setSingularFormFlag(false);
  }

  const handleMultipackClick = () => {
    resetSkuTypeFormFlags();
    setMultipackFormFlag(true);
  }

  const handleSingularClick = () => {
    resetSkuTypeFormFlags();
    setSingularFormFlag(true);
  }

  const handleByWeightClick = () => {
    resetSkuTypeFormFlags();
    setByWeightFormFlag(true);
  }

  const { t } = useTranslation();

  const get_sku_type_callback = ({ searchSkuType }) => {
    switch (searchSkuType) {
      case 'MultipackItem':
        return handleMultipackClick;
      case 'SingularItem':
        return handleSingularClick;
      case 'WeightyItem':
        return handleByWeightClick;
      default:
        return;
    }
  }

 if (signedIn) {
  const circle = circles.find(c => c.id == circle_id);
  console.log('SkuChooseType.js circle: ', circle)
  if (circle == null) {
    console.log('sct circle2: ', circle)
    // User likely used browser to refresh the page, lost access to state.
    // This causes 'circle' to be 'undefined'.
    // Let's go back to the 'skus' page.
    window.location.replace('/skus/');
  }


  console.log('SkuChooseType.js haveNotHandledUuidYet', haveNotHandledUuidYet);
  if (chosenUuid && haveNotHandledUuidYet) {
    // use the chosenUuid to locate the sku to copy. An 'off-' uuid is
    // a pseudo-sku staged from an OFF lookup, held apart from foundSkus.
    const search_sku =
      String(chosenUuid).startsWith('off-')
        ? stagedSku
        : foundSkus.find(s =>  s.uuuid == chosenUuid);
    console.log('SkuChooseType.js search_sku: ', search_sku)

    if (!search_sku) {
      console.log('SkuChooseType.js 2 search_sku: ', search_sku)
      // Maybe shopper added a new sku for a trip. To return to the trip they
      // clicked the Back button, but it's returning to a filled-in Sku form.
      // Its 'chosenUuid' value no longer exists, e.g. we are heading here:
// circles/018e7c50-223e-7a51-8340-0b137d4a4517/skus/da2bbe4f-f6ef-4a0f-a1bf-148697f00722
      // Grr, now we are stuck viewing a blank screen. Let's get out of here!
      navigate('/', {replace: true});

      // Refresh to update state, make sure future activity doesn't take us to
      // the processed chosenUuid:
      navigate(0);
  
      // I would have thought this line would cause the browser to start
      // showing '/trips/' but the view remains fixed at '/'.
      // And yet without this line, we see an ugly error upon clicking a Back
      // button. (We have just processed a Sku so that going to a url for
      // a nonexistent 'choenUuid' fails.)
      // Helpfully, if shopper clicks Back while now viewing '/', this line (?)
      // causes the '/trips/' view to display.
      navigate('/trips/', {replace: true});
    }

    // set the appropriate type of SkuForm based on search_sku's type.
    // A pseudo-sku staged from an OFF lookup ('off-...' chosenUuid)
    // has no type yet: get_sku_type_callback returns undefined, and we
    // leave the chooser showing so the shopper picks the type, with
    // SkuForm prefilling from the pseudo-sku's fields.
    // (search_sku can be undefined here — see the !search_sku case.)
    const handleClickTypeCallback =
      get_sku_type_callback({ searchSkuType: search_sku?.type });
    if (handleClickTypeCallback) {
      // now run the callback, e.g. handleMultipackClick()
      handleClickTypeCallback();
      setFocusSkuType(search_sku.type);
    }

    setHaveNotHandledUuidYet(false);
  }

  const searchOtherCirclesTippyContent =
      t('sku.searchOtherCirclesForSku.tippyContent')
    + t('sku.searchOtherCirclesForSku.tippyContentClickLink');
  // Maybe another Shopping Circle has created and shared information about a
  // Sku you want to set up. Click here to search for and copy their Sku.

  return (
      <article>
        <div ref={searchRef} tabIndex="0" />
        <HeadingBuilder
          Size        = 'h3'
          headingText = {t('sku.searchOtherCirclesForSku.headingText')}
          value       = {circle.identifier}
                      // Add Sku for Circle: uipuy
          tippyAlertText = {t('sku.circleAddSku.addSkuForCircleTippyContentAlert')}
          tippyText      = {t('sku.circleAddSku.addSkuForCircleTippyContentAlert')}
          tippyInfoText  = {t('sku.circleAddSku.addSkuForCircleTippyContentInfo')}
        />

        <HeadingBuilder
          tippyAlertText = {searchOtherCirclesTippyContent}
          tippyText      = {searchOtherCirclesTippyContent}
          linkUrl        = {`/circles/${circle_id}/search_skus`}
          goButtonText   = {Constants.GET_ICON_RT_ARROW}
          headingText    = {t('sku.searchOtherCirclesForSku.label')}
                         // Search Other Circles for Similar Sku
        />
        <br/>
        <HeadingBuilder
          Size = 'h4'
          headingText = {t('sku.skuChooseType.whatKindOfItem')}
          // What kind of item is this? (Click the best match below.)
        />
        <HeadingBuilder
          Size = 'h4'
          headingText = {t('sku.skuChooseType.howToDivide')}
          // When Shoppers share in a purchase, how will they divide the item?
        />

       <div className="wrapSkuChooseType">
        <div ref={singularSkuRef} tabIndex="0" >
          <SkuPic src={skuSingularSrc}
                  onClick={() => handleSingularClick()}
          />
        </div>
        <div>
          <HeadingBuilder
            // FIX ME: should be able to have/create a class that conveys 'bold':
            // containerType = 'text-bold' or 'fw-bold'
            // And then 'Size' can be set to 'h3' as expected.
            // Seems this way of 'h3' works via Tailwind.
            containerType = 'h3'
            Size = 'strong'
            headingText = {t('sku.singular_instructions.title')}
            // Singular
          />
          <HeadingBuilder
            headingText  = {t('sku.singular_instructions.copy')}
            paraPlayText = {t('sku.singular_instructions.copy')}
            // Shoppers will indicate a percentage showing...
          />
          <br/>
          <InAHurry showInAHurryButton={singularFormFlag}/>
          {singularFormFlag
            ? <SkuForm
                                circle_id={circle_id}
                                skuType={'SingularItem'}
                                resetSkuTypeFormFlags={resetSkuTypeFormFlags}
                                chosenUuid={chosenUuid}
                              />
            : <HeadingBuilder
                buttonAction   = {() => handleSingularClick()}
                headingText    = {t('sku.singular_instructions.buttonLabel')}
                // Sku is Singular
                tippyAlertText = {t('sku.singular_instructions.buttonTippyText')}
                tippyText      = {t('sku.singular_instructions.buttonTippyText')}
              />
          }
          <hr/>
        </div>

        <div ref={multipackSkuRef} tabIndex="0" >
          <SkuPic src={skuMultipackSrc}
                  onClick={() => handleMultipackClick()}
          />
        </div>
        <div>
          <HeadingBuilder
            // FIX ME: see above re 'bold'
            containerType = 'h3'
            Size = 'strong'
            headingText = {t('sku.multipack_instructions.title')}
            // Multipack
          />
          <HeadingBuilder
            headingText  = {t('sku.multipack_instructions.copy')}
            paraPlayText = {t('sku.multipack_instructions.copy')}
            // Shoppers will enter how many sub-units they want...
          />
          <br/>
          <InAHurry showInAHurryButton={multipackFormFlag}/>
          {multipackFormFlag
            ? <SkuForm
                                circle_id={circle_id}
                                skuType={'MultipackItem'}
                                resetSkuTypeFormFlags={resetSkuTypeFormFlags}
                                chosenUuid={chosenUuid}
                              />
            : <HeadingBuilder
                buttonAction   = {() => handleMultipackClick()}
                headingText    = {t('sku.multipack_instructions.buttonLabel')}
                // Sku is Multipack
                tippyAlertText = {t('sku.multipack_instructions.buttonTippyText')}
                tippyText      = {t('sku.multipack_instructions.buttonTippyText')}
              />
          }
          <hr/>
        </div>

        <div ref={weightySkuRef} tabIndex="0" >
          <SkuPic src={skuByWeightSrc}
                  onClick={() => handleByWeightClick()}
          />
        </div>
        <div>
          <HeadingBuilder
            // FIX ME: see above re 'bold'
            containerType = 'h3'
            Size = 'strong'
            headingText = {t('sku.weight_instructions.title')}
            // By Weight
          />
          <HeadingBuilder
            headingText  = {t('sku.weight_instructions.copy')}
            paraPlayText = {t('sku.weight_instructions.copy')}
            // For an item that is sold by weight...
          />
          <br/>
          <InAHurry showInAHurryButton={byWeightFormFlag}/>
          {byWeightFormFlag
            ? <SkuForm
                                circle_id={circle_id}
                                skuType={'WeightyItem'}
                                resetSkuTypeFormFlags={resetSkuTypeFormFlags}
                                chosenUuid={chosenUuid}
                              />
            : <HeadingBuilder
                buttonAction   = {() => handleByWeightClick()}
                headingText    = {t('sku.weight_instructions.buttonLabel')}
                // Sku is By Weight
                tippyAlertText = {t('sku.weight_instructions.buttonTippyText')}
                tippyText      = {t('sku.weight_instructions.buttonTippyText')}
              />
          }
        </div>
       </div>
      </article>
  )
 } else {
    return (
      <HeadingBuilder
        Size = 'h3'
        headingText = {t('global.notSignedInMessage')}
        // Not Authorized - Please Sign in or Sign up
      />
    )
 }
}

export default SkuChooseType;